import React, { useMemo, useState, useEffect } from "react";
import useApi from "../../CustomHooks/useApi";
import { financeAPI } from "../../../api/apiClient";
import getParticipantStatusStyle, { ParticipantStatus } from "../../../utils/getParticipantStatusStyle";
import "../../../css/Finance/FullRefundParticipantsSection.css";

const groupParticipantsByVariant = (participants) => {
  return participants.reduce((acc, participant) => {
    const key = participant.variantGuid;
    if (!key) return acc;

    if (!acc[key]) {
      acc[key] = {
        variantGuid: participant.variantGuid,
        variantName: participant.variantName || "Bez wariantu",
        participants: [],
      };
    }

    acc[key].participants.push(participant);
    return acc;
  }, {});
};

const AddFullRefundParticipantPopup = ({
  isOpen,
  availableParticipants,
  disabledParticipants,
  onClose,
  onAdd,
  savingParticipantGuid,
  isLoading,
}) => {
  const [search, setSearch] = useState("");
    useEffect(() => {
    if (!isOpen) return;
  
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
  
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
  
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [isOpen]);
  const filteredAvailableParticipants = useMemo(() => {
    const phrase = search.trim().toLowerCase();
    if (!phrase) return availableParticipants;

    return availableParticipants.filter((participant) => {
      const haystack = [
        participant.participantName,
        participant.reservationCode,
        participant.variantName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(phrase);
    });
  }, [availableParticipants, search]);

  const filteredDisabledParticipants = useMemo(() => {
    const phrase = search.trim().toLowerCase();
    if (!phrase) return disabledParticipants;

    return disabledParticipants.filter((participant) => {
      const haystack = [
        participant.participantName,
        participant.reservationCode,
        participant.variantName,
        participant.disabledReason,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(phrase);
    });
  }, [disabledParticipants, search]);

  const groupedAvailable = useMemo(() => {
    return Object.values(groupParticipantsByVariant(filteredAvailableParticipants)).sort((a, b) =>
      (a.variantName || "").localeCompare(b.variantName || "", "pl", {
        sensitivity: "base",
      })
    );
  }, [filteredAvailableParticipants]);

  const groupedDisabled = useMemo(() => {
    return Object.values(groupParticipantsByVariant(filteredDisabledParticipants)).sort((a, b) =>
      (a.variantName || "").localeCompare(b.variantName || "", "pl", {
        sensitivity: "base",
      })
    );
  }, [filteredDisabledParticipants]);

  if (!isOpen) return null;

  return (
    <div className="full-refund-popup-overlay" onClick={onClose}>
      <div className="full-refund-popup" onClick={(e) => e.stopPropagation()}>
        <div className="full-refund-popup-header">
          <div>
            <div className="full-refund-popup-title">
              Dodaj osobę do pełnego zwrotu
            </div>
            <div className="full-refund-popup-subtitle">
              Wybierz anulowaną osobę z rezerwacji wieloosobowej.
            </div>
          </div>

          <div className="full-refund-popup-close" onClick={onClose}>
            <i className="fa-solid fa-xmark" />
          </div>
        </div>

        <div className="full-refund-popup-search">
          <i className="fa-solid fa-magnifying-glass" />
          <input
            type="text"
            placeholder="Szukaj po osobie, kodzie lub wariancie"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="full-refund-popup-list">
          {groupedAvailable.length === 0 && groupedDisabled.length === 0 ? (
            <div className="draft-empty">Brak osób do pokazania.</div>
          ) : (
            <>
              {groupedAvailable.map((group) => (
                <div key={group.variantGuid} className="full-refund-popup-group">
                  <div className="full-refund-popup-group-title">{group.variantName}</div>

                  <div className="full-refund-popup-group-list">
                    {group.participants.map((participant) => {
                      const isSaving = savingParticipantGuid === participant.participantGuid;

                      return (
                        <div
                          key={participant.participantGuid}
                          className={`full-refund-popup-row ${isSaving ? "is-saving" : ""}`}
                          onClick={() => {
                            if (!isSaving && !isLoading) {
                              onAdd(participant);
                            }
                          }}
                        >
                        <div className="full-refund-popup-row-left">
                        <div
                            className={`participant-refund-status-icon ${
                            getParticipantStatusStyle(participant.participantStatus).statusClass
                            }`}
                            title={getParticipantStatusStyle(participant.participantStatus).label}
                        >
                            <i
                            className={`fa-solid ${getParticipantStatusStyle(participant.participantStatus).icon}`}
                            />
                        </div>

                        <div className="full-refund-popup-row-text">
                          <div className="full-refund-popup-row-name">
                            {participant.participantName || "—"}
                          </div>
                          {participant.isNnwExcludedFromFullRefund && (
                            <div className="full-refund-warning-note">
                              NNW wykupione – {participant.nnwCost?.toFixed?.(2) ?? participant.nnwCost} zł nie podlega zwrotowi
                            </div>
                          )}
                          <div className="full-refund-popup-row-meta">
                            <span className={`reg-code status-${participant.reservationStatus} reg-code-small`}>
                              {participant.reservationCode}
                            </span>
                            <span className="full-refund-popup-row-meta-name">
                              {participant.primaryParticipantName || "—"}
                            </span>
                          </div>
                        </div>
                        </div>

                          <div className="full-refund-popup-row-right">
                            {isSaving ? (
                              <i className="fa fa-spinner fa-spin" />
                            ) : (
                              <i className="fa-solid fa-plus" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {groupedDisabled.length > 0 && (
                <div className="participant-refund-disabled-section">
                  <div className="participant-refund-disabled-title">
                    Niedostępne
                  </div>

                  {groupedDisabled.map((group) => (
                    <div key={`disabled-${group.variantGuid}`} className="full-refund-popup-group">
                      <div className="full-refund-popup-group-title">{group.variantName}</div>

                      <div className="full-refund-popup-group-list">
                        {group.participants.map((participant) => (
                          <div
                            key={participant.participantGuid}
                            className="full-refund-popup-row is-disabled"
                          >
                            <div className="full-refund-popup-row-left">
                            <div
                                className={`participant-refund-status-icon ${
                                getParticipantStatusStyle(participant.participantStatus).statusClass
                                }`}
                                title={getParticipantStatusStyle(participant.participantStatus).label}
                            >
                                <i
                                className={`fa-solid ${getParticipantStatusStyle(participant.participantStatus).icon}`}
                                />
                            </div>

                              <div className="full-refund-popup-row-text">
                                <div className="full-refund-popup-row-name">
                                  {participant.participantName || "—"}
                                </div>
                                                                {participant.isNnwExcludedFromFullRefund && (
                                  <div className="full-refund-warning-note">
                                    NNW wykupione – {participant.nnwCost?.toFixed?.(2) ?? participant.nnwCost} zł nie podlega zwrotowi
                                  </div>
                                )}

                                <div className="full-refund-popup-row-meta">
                                  <span className={`reg-code status-${participant.reservationStatus} reg-code-small`}>
                                    {participant.reservationCode}
                                  </span>
                                  <span className="full-refund-popup-row-meta-name">
                                    {participant.primaryParticipantName || "—"}
                                  </span>
                                </div>


                              </div>
                            </div>

                            <div className="full-refund-popup-row-right">
                              <i className="fa-solid fa-ban" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const FullRefundParticipantsSection = ({
  tripGuid,
  draft,
  financialData,
  fullRefundReservationGuids,
  onDraftChanged,
  showToast,
  clearToast,
  canEdit
}) => {
  const { callApi, isLoading } = useApi();
  const [savingParticipantGuid, setSavingParticipantGuid] = useState(null);
  const [showAddPopup, setShowAddPopup] = useState(false);

  const participantRefundMap = useMemo(() => {
    const map = {};
    (draft?.participantRefunds ?? []).forEach((item) => {
      map[item.participantGuid] = item;
    });
    return map;
  }, [draft]);

  const fullRefundReservationSet = useMemo(
    () => new Set(fullRefundReservationGuids ?? []),
    [fullRefundReservationGuids]
  );

  const allEligibleCancelledParticipants = useMemo(() => {
    const participants = [];
    (financialData?.reservations ?? []).forEach((reservation) => {
      const hasAtLeastTwoParticipants = reservation?.participants.length >= 2;

      (reservation.participants ?? []).forEach((participant) => {
        if (participant.status !== ParticipantStatus.Cancelled) return;
        if (!hasAtLeastTwoParticipants) return;

        participants.push({
          participantGuid: participant.participantGuid || participant.guid,
          participantName: participant.name,
          participantStatus: participant.status,
          reservationGuid: reservation.reservationGuid,
          reservationCode: reservation.reservationCode,
          reservationStatus: reservation.status,
          primaryParticipantName: reservation.primaryParticipantName,
          variantGuid: reservation.variantGuid,
          variantName: reservation.variantName,
          hasNnwCoverage: !!participant.hasNnwCoverage,
          nnwCost: participant.nnwCost ?? 0,
          isNnwExcludedFromFullRefund: !!participant.isNnwExcludedFromFullRefund,
          isDisabled: fullRefundReservationSet.has(reservation.reservationGuid),
          disabledReason: fullRefundReservationSet.has(reservation.reservationGuid)
            ? "Pełny zwrot ustawiony dla całej rezerwacji"
            : null,
        });
      });
    });

    return participants.sort((a, b) =>
      (a.participantName || "").localeCompare(b.participantName || "", "pl", {
        sensitivity: "base",
      })
    );
  }, [financialData, fullRefundReservationSet]);

  const selectedParticipants = useMemo(() => {
    return allEligibleCancelledParticipants.filter(
      (p) => !!participantRefundMap[p.participantGuid]
    );
  }, [allEligibleCancelledParticipants, participantRefundMap]);

  const availableParticipants = useMemo(() => {
    return allEligibleCancelledParticipants.filter(
      (p) => !participantRefundMap[p.participantGuid] && !p.isDisabled
    );
  }, [allEligibleCancelledParticipants, participantRefundMap]);

  const disabledParticipants = useMemo(() => {
    return allEligibleCancelledParticipants.filter(
      (p) => !participantRefundMap[p.participantGuid] && p.isDisabled
    );
  }, [allEligibleCancelledParticipants, participantRefundMap]);

  const selectedGrouped = useMemo(() => {
    return Object.values(groupParticipantsByVariant(selectedParticipants)).sort((a, b) =>
      (a.variantName || "").localeCompare(b.variantName || "", "pl", {
        sensitivity: "base",
      })
    );
  }, [selectedParticipants]);

  const handleAddParticipantRefund = (participant) => {
    if (savingParticipantGuid || isLoading) return;

    setSavingParticipantGuid(participant.participantGuid);

    callApi(
      () =>
        financeAPI.setDraftParticipantFullRefund(tripGuid, {
          participantGuid: participant.participantGuid,
          isFullRefund: true,
          fullRefundReason: "Ustawione ręcznie przez osobę finansową",
          notes: null,
        }),
      async () => {
        await onDraftChanged();
        setSavingParticipantGuid(null);

        showToast(
          `Dodano pełny zwrot dla osoby ${participant.participantName}`,
          "success"
        );
      },
      () => {
        clearToast();
        setSavingParticipantGuid(null);
      }
    );
  };

  const handleDeleteParticipantRefund = (participantRefundId, participantName, participantGuid) => {
    if (savingParticipantGuid || isLoading) return;

    setSavingParticipantGuid(participantGuid);

    callApi(
      () => financeAPI.deleteDraftParticipantFullRefund(tripGuid, participantRefundId),
      async () => {
        await onDraftChanged();
        setSavingParticipantGuid(null);

        showToast(
          `Usunięto pełny zwrot dla osoby ${participantName}`,
          "success"
        );
      },
      () => {
        clearToast();
        setSavingParticipantGuid(null);
      }
    );
  };

  return (
    <div className="draft-card full-refund-picker-card">
      <div className="draft-card-title">2. Osoby do pełnego zwrotu</div>
      {canEdit && <><div className="draft-card-subtitle">
        Wybierz anulowane osoby z rezerwacji wieloosobowych, dla których ma zostać ustawiony pełny zwrot.
      </div>

      <div className="full-refund-picker-toolbar">
        <div
          className={`full-refund-add-btn ${
            availableParticipants.length === 0 ? "disabled" : ""
          }`}
          onClick={() => {
            if (availableParticipants.length > 0 || disabledParticipants.length > 0) {
              setShowAddPopup(true);
            }
          }}
        >
          <i className="fa-solid fa-plus" />
          <span>Dodaj osobę</span>
        </div>

        <div className="full-refund-picker-count">
          Wybrane: <strong>{selectedParticipants.length}</strong>
        </div>
      </div>
      </>
      }

      <div className="full-refund-selected-list">
        {selectedGrouped.length === 0 ? (
          <div className="draft-empty">
            Nie wybrano jeszcze żadnej osoby do pełnego zwrotu.
          </div>
        ) : (
          selectedGrouped.map((group) => (
            <div key={group.variantGuid} className="full-refund-selected-group">
              <div className="full-refund-selected-group-title">
                {group.variantName}
              </div>

              <div className="full-refund-selected-group-list">
                {group.participants.map((participant) => {
                  const isSaving = savingParticipantGuid === participant.participantGuid;
                  const participantRefund = participantRefundMap[participant.participantGuid];

                  return (
                    <div
                      key={participant.participantGuid}
                      className={`full-refund-selected-row ${isSaving ? "is-saving" : ""}`}
                    >
                    <div className="full-refund-selected-row-left">
                    <div
                        className={`participant-refund-status-icon ${
                        getParticipantStatusStyle(participant.participantStatus).statusClass
                        }`}
                        title={getParticipantStatusStyle(participant.participantStatus).label}
                    >
                        <i
                        className={`fa-solid ${getParticipantStatusStyle(participant.participantStatus).icon}`}
                        />
                    </div>

                    <div className="full-refund-selected-row-text">
                    <div className="full-refund-selected-row-name">
                        {participant.participantName || "—"}
                    </div>
                    {participant.isNnwExcludedFromFullRefund && (
                        <div className="full-refund-warning-note">
                          NNW wykupione – {participant.nnwCost?.toFixed?.(2) ?? participant.nnwCost} zł nie podlega zwrotowi
                        </div>
                      )}
                    <div className="full-refund-selected-row-meta">
                        <span className={`reg-code status-${participant.reservationStatus} reg-code-small`}>
                        {participant.reservationCode}
                        </span>
                        <span className="full-refund-selected-row-meta-name">
                        {participant.primaryParticipantName || "—"}
                        </span>
                    </div>
                    </div>
                    </div>

                      {canEdit && <div
                        className="full-refund-remove-icon"
                        onClick={() => {
                          if (!isSaving && !isLoading && participantRefund?.id) {
                            handleDeleteParticipantRefund(
                              participantRefund.id,
                              participant.participantName,
                              participant.participantGuid
                            );
                          }
                        }}
                      >
                        {isSaving ? (
                          <i className="fa fa-spinner fa-spin" />
                        ) : (
                          <i className="fa-solid fa-xmark" />
                        )}
                      </div>
                      }
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {canEdit && <AddFullRefundParticipantPopup
        isOpen={showAddPopup}
        availableParticipants={availableParticipants}
        disabledParticipants={disabledParticipants}
        savingParticipantGuid={savingParticipantGuid}
        isLoading={isLoading}
        onClose={() => {
          if (!savingParticipantGuid) {
            setShowAddPopup(false);
          }
        }}
        onAdd={handleAddParticipantRefund}
      />
      }
    </div>
  );
};

export default FullRefundParticipantsSection;